/* one shared observer for every reel on the page; rebuilt whenever the
   slider re-clones its slides */
let reelCdnObserver = null;

/* separate observer: assigns each video's real src (and starts loading
   its metadata) only once the slide is actually visible inside the
   section's clipped carousel window. Owl's loop clones enough slides
   to fill every breakpoint (up to ~15 <video> elements for 7 real
   reels), so giving every one of them a real src on page load is what
   was slow on mobile — this defers the network request until a slide
   is genuinely the one on screen. */
let reelCdnLoadObserver = null;

function loadReelVideoCdn(video) {

  if (video.dataset.srcLoaded) return;

  const src = video.dataset.srcCdn;

  if (!src) return;

  video.dataset.srcLoaded = 'true';

  video.preload = 'metadata';
  video.src = src;
  video.load();

}

function initCustomVideoPlayerCdn() {

  const wrappers = document.querySelectorAll('.js-reel-video-cdn');

  if (!wrappers.length) return;

  /* Owl clones slides to build the loop, so the reel list changes after
     every (re)build — always read it fresh instead of caching an array. */
  function allReelVideos() {

    return Array.from(
      document.querySelectorAll('.video-reels-cdn__video')
    );

  }

  /* ── pause all except current ── */
  function pauseAllExcept(current) {

    allReelVideos().forEach((v) => {

      if (v !== current) {

        v.pause();

        const parent =
          v.closest('.js-reel-video-cdn');

        parent?.classList.remove('is-playing');

      }

    });

  }

  wrappers.forEach((wrapper) => {

    if (wrapper.dataset.initialized) return;

    wrapper.dataset.initialized = 'true';

    const video =
      wrapper.querySelector('.video-reels-cdn__video');

    const playBtn =
      wrapper.querySelector('.video-reels-cdn__play-icon');

    const seek =
      wrapper.querySelector('.video-reels-cdn__seek');

    if (!video) return;

    /* fall back to thumbnails.jpg if thumbnail.jpg is missing on the CDN */
    const posterFallback = video.dataset.posterFallback;

    if (posterFallback && video.poster) {

      const posterCheck = new Image();

      posterCheck.onerror = () => {
        video.poster = posterFallback;
      };

      posterCheck.src = video.poster;

    }

    /* auto resolution: cascade through progressively lower renditions
       if the configured quality hasn't finished encoding yet */
    let srcFallbacks = [];

    try {
      srcFallbacks = JSON.parse(video.dataset.srcFallback || '[]');
    } catch (e) {}

    if (srcFallbacks.length) {

      video.addEventListener('error', function tryNextSrcCdn() {

        const nextSrc = srcFallbacks.shift();

        if (!nextSrc) {
          video.removeEventListener('error', tryNextSrcCdn);
          return;
        }

        video.src = nextSrc;
        video.load();

      });

    }

    /* force first frame on iOS */
    video.addEventListener('loadedmetadata', () => {

      try {
        video.currentTime = 0.01;
      } catch (e) {}

    });

    video.addEventListener('seeked', () => {

      if (!wrapper.classList.contains('is-playing')) {
        video.pause();
      }

    });

    /* ── play / pause ── */
    function togglePlay() {

      /* backstop: the tap that starts playback also guarantees src exists,
         in case a slide was somehow never caught by the visibility
         observer (e.g. a rapid programmatic jump) */
      loadReelVideoCdn(video);

      if (video.paused) {

        pauseAllExcept(video);

        wrapper.classList.add('is-playing');

        /* iOS Safari can reject play() (e.g. interrupted by a
           near-simultaneous pause) — if it does, undo the optimistic
           class so the play icon doesn't stay stuck hidden */
        video.play()?.catch(() => {
          wrapper.classList.remove('is-playing');
        });

      } else {

        video.pause();

        wrapper.classList.remove('is-playing');

      }

    }

    /* play button */
    playBtn?.addEventListener('click', (e) => {

      e.stopPropagation();

      togglePlay();

    });

    /* click on video */
    video.addEventListener('click', togglePlay);

    /* update seekbar */
    video.addEventListener('timeupdate', () => {

      if (video.duration) {

        seek.value =
          (video.currentTime / video.duration) * 100;

      }

    });

    /* seek */
    seek?.addEventListener('input', () => {

      if (video.duration) {

        video.currentTime =
          (seek.value / 100) * video.duration;

      }

    });

    /* pause state */
    video.addEventListener('pause', () => {

      wrapper.classList.remove('is-playing');

    });

    /* ended state */
    video.addEventListener('ended', () => {

      wrapper.classList.remove('is-playing');

    });

  });

  function pauseVideo(video) {

    video.pause();

    video
      .closest('.js-reel-video-cdn')
      ?.classList.remove('is-playing');

  }

  /* ── auto pause when scrolled out of view ── */
  const MIN_VISIBLE = 0.35;

  if (reelCdnObserver) reelCdnObserver.disconnect();

  reelCdnObserver = new IntersectionObserver((entries) => {

    entries.forEach((entry) => {

      /* A portrait reel can be taller than the viewport, and then its ratio
         never reaches MIN_VISIBLE — the threshold is unreachable, so no
         crossing is ever reported. For those, fall back to "fully off
         screen". */
      const rootHeight = entry.rootBounds ? entry.rootBounds.height : 0;

      const tallerThanViewport =
        rootHeight > 0 &&
        entry.boundingClientRect.height > rootHeight * 0.9;

      const scrolledAway = tallerThanViewport
        ? !entry.isIntersecting
        : entry.intersectionRatio < MIN_VISIBLE;

      if (scrolledAway) {
        pauseVideo(entry.target);
      }

    });

  }, {
    threshold: [0, MIN_VISIBLE]
  });

  allReelVideos().forEach((video) => {
    reelCdnObserver.observe(video);
  });

  /* ── load each reel's src only once it's actually inside the section's
     clipped carousel window (see loadReelVideoCdn above). `.video-reels-cdn`
     has `overflow: clip`, so an off-stage clone is genuinely clipped out of
     the render tree and correctly reports as non-intersecting — only the
     1–4 slides on screen at a given breakpoint ever load. ── */
  if (reelCdnLoadObserver) reelCdnLoadObserver.disconnect();

  reelCdnLoadObserver = new IntersectionObserver((entries) => {

    entries.forEach((entry) => {

      if (entry.isIntersecting) {
        loadReelVideoCdn(entry.target);
      }

    });

  }, {
    threshold: 0,
  });

  allReelVideos().forEach((video) => {
    reelCdnLoadObserver.observe(video);
  });

  /* leaving the tab counts as scrolling past it */
  if (!document.body.dataset.reelCdnVisibilityBound) {

    document.body.dataset.reelCdnVisibilityBound = 'true';

    document.addEventListener('visibilitychange', () => {

      if (!document.hidden) return;

      document
        .querySelectorAll('.video-reels-cdn__video')
        .forEach(pauseVideo);

    });

  }

}

/* ── pause every reel video (used on slider nav click / swipe) ── */
function pauseAllReelVideosCdn() {

  document
    .querySelectorAll('.video-reels-cdn__video')
    .forEach((video) => {

      video.pause();

      const wrapper = video.closest('.js-reel-video-cdn');

      wrapper?.classList.remove('is-playing');

      /* iOS Safari defers repainting non-essential style changes
         until an active touch/swipe gesture settles — force a
         reflow so the play icon shows up immediately instead */
      if (wrapper) void wrapper.offsetHeight;

    });

}

/* Owl builds the loop from cloned slides. A clone carries the original's
   data-initialized flag (and, once a slide has already loaded, its
   now-populated src/data-src-loaded too) but none of its listeners, so
   clear the initialized flag and let initCustomVideoPlayerCdn wire the
   clone up like any other reel; the disconnect+re-observe in there also
   gives a clone its own fresh visibility check for lazy-loading. */
function wireClonedReelsCdn(sliderCdn) {

  sliderCdn
    .querySelectorAll('.owl-item.cloned .js-reel-video-cdn')
    .forEach((wrapper) => {

      delete wrapper.dataset.initialized;

      wrapper.classList.remove('is-playing');

    });

  initCustomVideoPlayerCdn();

}

function initVideoReelsCdnSlider() {

  if (typeof jQuery === 'undefined' || !jQuery.fn.owlCarousel) return;

  jQuery('.video-reels-cdn__slider').each(function () {

    const $sliderCdn = jQuery(this);

    if ($sliderCdn.data('owl.carousel')) return;

    const $scopeCdn = $sliderCdn.closest('.video-reels-cdn');

    $sliderCdn.owlCarousel({
      loop: true,
      startPosition: 2,
      margin: 0,
      nav: false,
      dots: false,
      smartSpeed: 800,
      touchDrag: true,
      mouseDrag: true,
      pullDrag: false,
      freeDrag: false,
      responsiveRefreshRate: 200,
      responsive: {
        0: { items: 1 },
        768: { items: 2 },
        992: { items: 3 },
        1200: { items: 4 },
      },
    });

    wireClonedReelsCdn(this);

    /* a breakpoint change throws the old clones away and makes new ones */
    $sliderCdn.on('refreshed.owl.carousel', () => {
      wireClonedReelsCdn(this);
    });

    /* nav click or swipe — stop whatever was playing before the track moves */
    $sliderCdn.on(
      'drag.owl.carousel translate.owl.carousel',
      pauseAllReelVideosCdn
    );

    $scopeCdn.find('.video-reels-cdn__nav-btn--prev').on('click', () => {
      $sliderCdn.trigger('prev.owl.carousel');
    });

    $scopeCdn.find('.video-reels-cdn__nav-btn--next').on('click', () => {
      $sliderCdn.trigger('next.owl.carousel');
    });

  });

}

function initVideoReelsCdn() {

  initCustomVideoPlayerCdn();

  initVideoReelsCdnSlider();

}

document.addEventListener(
  'DOMContentLoaded',
  initVideoReelsCdn
);

document.addEventListener(
  'shopify:section:load',
  initVideoReelsCdn
);